import { useId, useState, useEffect } from 'react';
import { loadScript, debounce } from '@utils/common';
import { useComponentEntry } from '@utils/component-framework';
import { useUpdateGeogebraEntryMutation } from '../state/mutation.update-entry-value.js';

function useGGBEntry(props) {
  const data = useComponentEntry(props.component.entryKey, {
    select: entry => entry?.data.base64
  }).data;
  return [data ?? props.component.data.ggbBase64];
}
function useGGBApplet(props, applet) {
  const id = useId();
  const updateEntryMutation = useUpdateGeogebraEntryMutation(props.component.entryKey);
  const [initialBase64] = useGGBEntry(props);
  const [ggbApplet, setGgbApplet] = useState();

  // mount the GGB applet in a side effect when the component is mounted
  useEffect(() => {
    if (!initialBase64) {
      return;
    }
    const ggbListenerName = `__gyldendal_geogebra__${Date.now()}`;
    const ggbPromise = loadScript("https://www.geogebra.org/apps/latest/deployggb.js").then(() => {
      if (!applet.appletRef.current) {
        return;
      }

      // create a global update listener, which extracts current state and
      // saves it as an entry so that we can remember user inputs
      window[ggbListenerName] = debounce(() => {
        if (props.component.data.flowType === "random") {
          // never attempt to save data for randomly evaluated applications
          return;
        }
        const api = ggb.getAppletObject() ?? originalApi;
        if (!api) {
          // bail out if the GGB Applet has been unmounted before we managed
          // to save changes
          return;
        }
        const result = (() => {
          if (props.component.data.flowType === "investigating") {
            // for investigating components, we never attempt to extract a
            // result as they inherently cannot be validated
            return undefined;
          }
          return {
            status: api.getValue("gyldendal__completed") ? "completed" : "started",
            score: {
              pointsGotten: api.getValue("gyldendal__points_gotten") || 0,
              pointsTotal: api.getValue("gyldendal__points_total") || 0
            }
          };
        })();
        updateEntryMutation(api.getBase64(), result);
      }, 500);

      // create the Geogebra applet and inject it into the page
      let originalApi;

      // Ensure our container is properly reset before applying the
      // (potentially replacement) applet
      applet.appletRef.current.style.height = "";
      applet.appletRef.current.style.width = "";
      const ggb = new GGBApplet({
        ...props.component.data.settings,
        appletOnLoad(api) {
          api.setBase64(initialBase64);
          api.registerAddListener(ggbListenerName);
          api.registerClearListener(ggbListenerName);
          api.registerClientListener(ggbListenerName);
          api.registerRemoveListener(ggbListenerName);
          api.registerRenameListener(ggbListenerName);
          api.registerStoreUndoListener(ggbListenerName);
          api.registerUpdateListener(ggbListenerName);
          originalApi = api;
          setGgbApplet(api);
        },
        id: `ggb_${id}`,
        scaleContainerClass: applet.appletClassName,
        allowUpscale: true,
        showFullscreenButton: true,
        borderColor: "#f2f2f2"
      });
      ggb.inject(applet.appletRef.current);
      return ggb;
    });

    // ensure correct teardown to avoid memory leaks of the applet
    return () => {
      ggbPromise.then(ggb => {
        const api = ggb?.getAppletObject();
        api?.unregisterAddListener?.(ggbListenerName);
        api?.unregisterClearListener?.(ggbListenerName);
        api?.unregisterClientListener?.(ggbListenerName);
        api?.unregisterRemoveListener?.(ggbListenerName);
        api?.unregisterRenameListener?.(ggbListenerName);
        api?.unregisterStoreUndoListener?.(ggbListenerName);
        api?.unregisterUpdateListener?.(ggbListenerName);
        api?.remove?.();

        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete window[ggbListenerName];
      });
    };
  },
  // intentionally run mount effect only initially, as we do not want to
  // re-render the Geogebra applet on user changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [props.component.entryKey]);
  return [ggbApplet];
}

export { useGGBApplet };
//# sourceMappingURL=hook.ggb-applet.js.map
